import styled from '@emotion/styled';
import { faTooth } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Fragment } from 'react/jsx-runtime';
import { useHistory, useLocation, useParams } from 'react-router';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import useKeypress from 'react-use-keypress';

import routes from '../../../../routes/routes.constants';
import { blueUltramarine100 } from '../../../../theme/videa-style-tokens';
import { useIsEmbedded } from '../../../../utils/hooks';
import { useUniversalToFDI } from '../../../../utils/internationalization/useUniversalToFDI';
import { selectTeethAtom } from '../../../patientList/atoms';
import { useTrackImageIndex } from '../../multiFileViewer/findingViewer/imageBrowser/helpers';
import { ALL_KEYS, KEY_MAP } from '../../utils';
import {
  activeImageIsSelectedAtom,
  annotateActionAtom,
  disableKeyPressAtom,
  selectedFindingsAtom,
} from '../atoms';

const ImageTracker = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;

  .button {
    height: 2em;
    line-height: 2em;
    font-size: 1em;
    z-index: 100;
    color: ${blueUltramarine100};
    &:hover {
      color: white;
    }
  }
  .imageIndexText {
    display: flex;
    flex-direction: row;
    gap: 1em;
    height: 2em;
    line-height: 2em;
    font-size: 1em;
    color: white;
    z-index: 100;
    white-space: 'nowrap';
    padding: 0 0 0 0.5em;
  }
  .imageFilterLabel {
    margin: 0 1em 0 0.5em;
  }
`;

export const ToothLabel = () => {
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const { displayToothNumber } = useUniversalToFDI();

  return (
    <div className="vai-rounded-3xl vai-px-3 vai-flex vai-items-center vai-gap-2 vai-bg-zinc-700">
      <FontAwesomeIcon icon={faTooth} />
      <p className="vai-font-inter">
        {selectedTeeth
          .map(displayToothNumber)
          .sort((a, b) => (parseInt(a) < parseInt(b) ? -1 : 1))
          .map((x, i) => {
            return (
              <Fragment key={x}>
                {x}
                {i !== selectedTeeth.length - 1 && ', '}
              </Fragment>
            );
          })}
      </p>
    </div>
  );
};

export const CasePresentationImageTracker = () => {
  const { id: selectedPatientId, imageId: activeImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const selectedPhotoId = params.get('selected-photo');

  const nextImageIds = useTrackImageIndex();
  const nextSideBySideImageIds = useTrackImageIndex(selectedPhotoId);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const singleFinding = useAtomValue(selectedFindingsAtom);
  const shouldDisableKeyPress = useAtomValue(disableKeyPressAtom);
  const isEmbedded = useIsEmbedded();
  const isActiveImageSelected = useAtomValue(activeImageIsSelectedAtom);
  const annotate = useAtomValue(annotateActionAtom);

  const { t } = useLingui();

  const currentIndex = nextImageIds?.currentIndex;
  const totalImages = nextImageIds?.totalImages;

  const shouldChangeActiveImage = !selectedPhotoId || isActiveImageSelected;

  const setSelectedImage = (id?: string) => {
    if (!id) {
      return;
    }
    const newRoute = isEmbedded
      ? `/embedded${routes.ANNOTATOR}/${selectedPatientId}/${id}`
      : `${routes.ANNOTATOR}/${selectedPatientId}/${id}`;
    history.push({ pathname: newRoute, search: history.location.search });
  };

  const setSelectedSideBySideImage = (id?: string) => {
    if (!id) {
      return;
    }
    const queryParams = new URLSearchParams({ 'selected-photo': id });
    history.replace({ search: queryParams.toString() });
  };

  const onPreviousClick = () => {
    if (annotate) {
      return;
    }
    if (!shouldChangeActiveImage) {
      setSelectedSideBySideImage(
        nextSideBySideImageIds?.prev === activeImageId
          ? nextSideBySideImageIds?.skipPrev
          : nextSideBySideImageIds?.prev,
      );
    } else {
      setSelectedImage(
        nextImageIds?.prev === selectedPhotoId
          ? nextImageIds?.skipPrev
          : nextImageIds?.prev,
      );
    }
  };

  const onNextClick = () => {
    if (annotate) {
      return;
    }
    if (!shouldChangeActiveImage) {
      setSelectedSideBySideImage(
        nextSideBySideImageIds?.next === activeImageId
          ? nextSideBySideImageIds?.skipNext
          : nextSideBySideImageIds?.next,
      );
    } else {
      setSelectedImage(
        nextImageIds?.next === selectedPhotoId
          ? nextImageIds?.skipNext
          : nextImageIds?.next,
      );
    }
  };

  useKeypress(ALL_KEYS, (event: { key: any }) => {
    if (shouldDisableKeyPress) {
      return;
    }

    switch (true) {
      case KEY_MAP.keyPressedNextImage.includes(event.key):
        onNextClick();
        break;
      case KEY_MAP.keyPressedPrevImage.includes(event.key):
        onPreviousClick();
        break;
    }
  });

  return (
    <>
      <ImageTracker>
        <IconButton
          onClick={onPreviousClick}
          aria-label="Previous"
          size="large"
          className="button"
        >
          <Tooltip title={t`Go to previous image`}>
            <ArrowBackIcon />
          </Tooltip>
        </IconButton>
        <div className="imageIndexText">
          <Typography>
            <span className="imageFilterLabel">
              <Trans>Images</Trans>
            </span>
            {currentIndex !== undefined ? currentIndex + 1 : currentIndex} /{' '}
            {totalImages}
          </Typography>
          {!!selectedTeeth.length && !singleFinding && <ToothLabel />}
        </div>

        <IconButton
          onClick={onNextClick}
          aria-label="Next"
          size="large"
          className="button"
        >
          <Tooltip title={t`Go to next image`}>
            <ArrowForwardIcon />
          </Tooltip>
        </IconButton>
      </ImageTracker>
    </>
  );
};
